/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import IManufacturer from '@/models/vehicle/interfaces/manufacturer.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import IDerivative from '@/models/vehicle/interfaces/derivative.d';
import CampaignVehicleLevel from '@/enums/CampaignVehicleLevel';
import VehicleType from '@/enums/VehicleType';
import { PublisherService } from '@/services/PublisherService';
import Derivative from '../models/vehicle/derivative';
import SearchQuery from '../models/SearchQuery';
import Results from '../models/vehicle/results';
import { initialSettingsService } from './InitialSettingsService';

// This has been added to help filter fuel types
// so that only the short hand name will appear
// on the forms e.g electric/hybrid
const hybridTypes = [
  'DIESEL/ELECTRIC HYBRID',
  'DIESEL/PLUGIN ELEC HYBRID',
  'PETROL/ELECTRIC HYBRID',
  'PETROL/PLUGIN ELEC HYBRID',
  'HYDROGEN FUEL CELL',
  'DIESEL SERIES PHEV',
  'PETROL SERIES PHEV',
  'PETROL PARALLEL PHEV',
  'DIESEL PARALLEL PHEV'
];

const petrolTypes = [
  'PETROL/BIO ETHANOL (E85)',
  'PETROL/CNG',
  'PETROL/LPG'
];

function shouldRestrict(level: CampaignVehicleLevel, target: 'Manufacturer' | 'Range' | 'Derivative'): boolean {
  if (level === CampaignVehicleLevel.None) return false;
  if (target === 'Manufacturer') return level === CampaignVehicleLevel.Manufacturer || level === CampaignVehicleLevel.Range;
  if (target === 'Range') return level === CampaignVehicleLevel.Range;
  if (target === 'Derivative') return level === CampaignVehicleLevel.Range;
  return false;
}

const petrolFuelType = 'Petrol';
const hybridFuelType = 'Hybrid';
const hybridFuelTypeAlternative = 'PHEV';

class VehicleSearchService {
  private static serviceInstance: VehicleSearchService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  private vdsBaseUrl(vehicleType: VehicleType, path: string): string {
    return `https://vehicles.uk-tla.com/api/v1/${vehicleType === VehicleType.Light ? 'Van' : 'Car'}/search/${path}`;
  }

  public async fetchManufacturers(
    searchQuery: SearchQuery
  ): Promise<IManufacturer[]> {
    const restrictVehicles = shouldRestrict(searchQuery.campaignVehicleLevel, 'Manufacturer');
    const results: IManufacturer[] = await this.baseFetch(this.vdsBaseUrl(searchQuery.vehicleType, 'Manufacturer'), searchQuery, restrictVehicles).then((json: any) => json.items as IManufacturer[]);

    if (searchQuery.fuelTypes && searchQuery.fuelTypes.length > 0) {
      results.forEach((x) => {
        const fuelTypes: string[] = [];
        x.fuelTypes.forEach((fuelType) => {
          if (hybridTypes.indexOf(fuelType.toUpperCase()) > -1) {
            if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(hybridFuelType))) {
              fuelTypes.push(hybridFuelType);
            }
          } else if (petrolTypes.indexOf(fuelType.toUpperCase()) > -1) {
            if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(petrolFuelType))) {
              fuelTypes.push(petrolFuelType);
            }
          } else if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(fuelType))) {
            fuelTypes.push(fuelType);
          }
        });

        x.fuelTypes = fuelTypes;
      });
    }

    let filteredManufacturers = results.filter((manufacturer) => manufacturer.fuelTypes.length > 0);

    const restriction = initialSettingsService.restrictionQueryString();

    if (restriction != null && restriction.includes('mini')) {
      // MINI Only.
      filteredManufacturers = filteredManufacturers.filter((x) => x.manufacturerId === 73);
    }

    return filteredManufacturers;
  }

  public async fetchRanges(searchQuery: SearchQuery): Promise<IRange[]> {
    const restrictVehicles = shouldRestrict(searchQuery.campaignVehicleLevel, 'Range');
    const results: IRange[] = await this.baseFetch(this.vdsBaseUrl(searchQuery.vehicleType, 'Range'), searchQuery, restrictVehicles).then((json: any) => json.items as IRange[]);

    if (searchQuery.fuelTypes && searchQuery.fuelTypes.length > 0) {
      results.forEach((x) => {
        const fuelTypes: string[] = [];

        x.fuelTypes.forEach((fuelType) => {
          if (hybridTypes.indexOf(fuelType.toUpperCase()) > -1) {
            if ((searchQuery.fuelTypes && (searchQuery.fuelTypes.includes(hybridFuelType) || searchQuery.fuelTypes.includes(hybridFuelTypeAlternative)))) {
              fuelTypes.push(hybridFuelType);
            }
          } else if (petrolTypes.indexOf(fuelType.toUpperCase()) > -1) {
            if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(petrolFuelType))) {
              fuelTypes.push(petrolFuelType);
            }
          } else if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(fuelType))) {
            fuelTypes.push(fuelType);
          }
        });

        x.fuelTypes = fuelTypes;
      });
    }

    let filteredRanges = results.filter((range) => range.fuelTypes.length > 0);

    const restriction = initialSettingsService.restrictionQueryString();

    if (restriction == null || restriction.length === 0) {
      return filteredRanges;
    }

    if (restriction === 'mini-new-gen') {
      // Cooper Electric - 1597
      // Coutryman ICE - 958
      const desiredRangeIds = [1597, 958];
      filteredRanges = filteredRanges.filter((x) => desiredRangeIds.includes(x.rangeId));
    }

    return filteredRanges;
  }

  public async fetchResults(searchQuery: SearchQuery): Promise<Results> {
    const restrictVehicles = shouldRestrict(searchQuery.campaignVehicleLevel, 'Derivative');

    return this.baseFetch(this.vdsBaseUrl(searchQuery.vehicleType, 'Derivative'), searchQuery, restrictVehicles)
      .then((json: any) => {
        const results = new Results();
        const filteredDerivatives: Derivative[] = [];
        results.items = json.items;

        if (results.items !== null) {
          results.items.forEach((vehicle: Derivative) => {
            if (hybridTypes.indexOf(vehicle.fuelTypeName.toUpperCase()) > -1) {
              vehicle.fuelTypeName = hybridFuelType;

              if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(hybridFuelType)) || !searchQuery.fuelTypes) {
                filteredDerivatives.push(vehicle);
              }
            }

            if (petrolTypes.indexOf(vehicle.fuelTypeName.toUpperCase()) > -1) {
              vehicle.fuelTypeName = petrolFuelType;

              if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(petrolFuelType)) || !searchQuery.fuelTypes) {
                filteredDerivatives.push(vehicle);
              }
            } else if ((searchQuery.fuelTypes && searchQuery.fuelTypes.includes(vehicle.fuelTypeName)) || !searchQuery.fuelTypes) {
              filteredDerivatives.push(vehicle);
            }
          });

          results.items = filteredDerivatives;
        }

        const bodyStyles = results.items
          .filter((x: Derivative) => x && x.bodystyles && x.bodystyles.length > 0)
          .map((x: Derivative) => x.bodystyles[0]);

        results.bodystyles = [...new Set(bodyStyles)];

        const models = results.items
          .map((v) => ({ modelId: v.modelId, modelName: v.modelName }))
          .filter((value, index, self) => self.findIndex((v) => v.modelId === value.modelId) === index)
          .sort((a, b) => a.modelName.localeCompare(b.modelName));

        results.models = [...new Set(models)];

        return results;
      });
  }

  public fetchFilteredCustomisationResults(
    searchQuery: SearchQuery,
    vehicles: IDerivative[] | null
  ): IDerivative[] {
    if (vehicles === null) {
      return [];
    }

    let fuelTypeFound: boolean = false;
    let bodyStyleFound: boolean = false;
    let trimFound: boolean = false;

    const vehicleList = vehicles.filter((vehicle) => {
      if (searchQuery.fuelTypes !== null) {
        for (const fType of searchQuery.fuelTypes) {
          fuelTypeFound = vehicle.fuelTypeName.toLowerCase().indexOf(fType.toLowerCase()) !== -1;
          if (fuelTypeFound === true) {
            break;
          }
        }
      }

      if (searchQuery.bodystyles !== null) {
        for (const bsType of searchQuery.bodystyles) {
          bodyStyleFound = vehicle.bodystyles.indexOf(bsType) !== -1;
          if (bodyStyleFound === true) {
            break;
          }
        }
      }

      if (searchQuery.trims !== null) {
        for (const tType of searchQuery.trims) {
          trimFound = vehicle.trimName === tType;
          if (trimFound === true) {
            break;
          }
        }
      }

      return (
        (searchQuery.bodystyles === null || searchQuery.bodystyles.length === 0 || bodyStyleFound)
        && (searchQuery.fuelTypes === null || searchQuery.fuelTypes.length === 0 || fuelTypeFound)
        && (searchQuery.trims === null || searchQuery.trims.length === 0 || trimFound)
        && (searchQuery.transmissions === null || vehicle.transmissionName === searchQuery.transmissions)
        && (searchQuery.doors === null || vehicle.doors === searchQuery.doors)
        && (searchQuery.engineSize === null || vehicle.engineSize === searchQuery.engineSize)
        && (searchQuery.modelId === null || searchQuery.modelId === 0 || searchQuery.modelId === vehicle.modelId)
      );
    });

    return vehicleList;
  }

  private baseFetch(url: string, params: SearchQuery, restrictVehicles: boolean = false): any {
    const qs = this.toQueryString(params);
    const vdsUrl = `${url}?${qs}${restrictVehicles ? `&SourceBrand=${encodeURIComponent(PublisherService.publisherName)}` : ''}`;

    return fetch(vdsUrl)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.statusText);
      })
      .catch((error) => {
        console.error('There was an issue calling a service', error.message);
      });
  }

  private toQueryString(paramsObject: SearchQuery): string {
    function toKeyValueQueryString({ key, value }: { key: string; value: string | string[]; }): any {
      if (value !== null) {
        if (Array.isArray(paramsObject[key])) {
          const values: string[] = [];
          const arrayValues = value as any[];

          if (key === 'fuelTypes' && arrayValues.includes(hybridFuelType)) {
            arrayValues.push(hybridFuelTypeAlternative);
          }
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < arrayValues.length; i++) {
            values.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(arrayValues[i])}`
            );
          }
          return values.join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(
          paramsObject[key]
        )}`;
      }
    }
    return Object.keys(paramsObject)
      .filter((key) => !!paramsObject[key]) // Use truthy/falsy to remove null values.
      .map((key) => toKeyValueQueryString({ key, value: paramsObject[key] }))
      .join('&');
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const vehicleSearchService = VehicleSearchService.instance;
